import { Anchor, Alert, Button, Code, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { IconCornerDownLeft } from '@tabler/icons-react';
import { useEffect, useRef } from 'react';
import { z } from 'zod';
import { isLocalhostLike, LOCALHOST_TUNNEL_DOC as LOCALHOST_TUNNEL_DOC_URL } from '~/utils/url';

export default function URLEntry({
  onSubmit,
  autoFocus = false,
}: {
  onSubmit: (url: string) => void;
  autoFocus?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const form = useForm({
    initialValues: {
      url: '',
    },
    validate: zod4Resolver(
      z.object({
        url: z
          .string()
          .min(1, 'URL is required')
          .refine((val) => {
            // Auto-prepend https:// if no protocol is provided for validation
            const urlWithProtocol = !val.startsWith('http://') && !val.startsWith('https://') ? `https://${val}` : val;

            try {
              new URL(urlWithProtocol);
              return true;
            } catch {
              return false;
            }
          }, 'Please enter a valid URL')
          .refine((val) => {
            // Block localhost-like values
            return !isLocalhostLike(val);
          }, 'To test local apps, install the Stably CLI and run "stably dev" to create a tunnel.'),
      }),
    ),
  });

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      // Use a small timeout to ensure the modal is fully rendered
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [autoFocus]);

  const handleSubmit = ({ url }: { url: string }) => {
    // Auto-prepend https:// if no protocol is provided
    const urlWithProtocol = !url.startsWith('http://') && !url.startsWith('https://') ? `https://${url}` : url;
    onSubmit(urlWithProtocol);
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <div className="flex flex-col gap-2">
        <TextInput
          ref={inputRef}
          label="Enter URL"
          placeholder="example.com"
          {...form.getInputProps('url')}
        />
        {isLocalhostLike(form.values.url) && (
          <Alert
            color="yellow"
            radius="md"
          >
            To test local apps, install the <Code>Stably CLI</Code> and run <Code>stably dev</Code> to start a localhost
            tunnel.{' '}
            <Anchor
              href={LOCALHOST_TUNNEL_DOC_URL}
              target="_blank"
              rel="noreferrer"
            >
              Learn more
            </Anchor>
            .
          </Alert>
        )}
        <div className="flex justify-end">
          <Button
            type="submit"
            size="compact-sm"
            rightSection={<IconCornerDownLeft />}
            disabled={!form.isValid() || isLocalhostLike(form.values.url)}
          >
            Submit
          </Button>
        </div>
      </div>
    </form>
  );
}
